import React from 'react';
import { ShieldCheck, LogOut, Building2, User as UserIcon, Tag, ArrowRight } from 'lucide-react';
import { AuthUser } from '../types';

interface NavbarProps {
  user: AuthUser | null;
  onLogout: () => void;
  onOpenSecurityModal: () => void;
  onOpenContactModal?: () => void;
  onNavigatePricing?: () => void;
  onLogin?: () => void;
  domain: string;
}

export const Navbar: React.FC<NavbarProps> = ({
  user,
  onLogout,
  onOpenSecurityModal,
  onOpenContactModal,
  onNavigatePricing,
  onLogin,
  domain,
}) => {
  return (
    <header className="global-brand-header">
      <div className="global-brand-header-inner">
        {/* Left Side: CerebroLabs Brand & CerebroSign Workspace Edition Label */}
        <div className="global-brand-left">
          {/* Logo CerebroLabs with link to https://cerebrolabs.pl */}
          <a
            href="https://cerebrolabs.pl"
            target="_blank"
            rel="noopener noreferrer"
            className="brand-cerebro-link"
            title="Przejdź do oficjalnej strony CerebroLabs.pl"
          >
            <img
              src="/cerebrolabs-logo.png"
              alt="CerebroLabs Logo"
              className="brand-cerebro-logo-img"
            />
            <span className="brand-cerebro-text">
              CerebroLabs<span className="tld">.pl</span>
            </span>
          </a>

          <div className="brand-header-divider" aria-hidden="true" />

          {/* Product Label: CerebroSign Workspace Edition */}
          <div className="brand-product-label">
            <span className="brand-product-name">CerebroSign</span>
            <span className="brand-product-badge">Workspace Edition</span>
          </div>
        </div>

        {/* Right Side: Pricing link, Security link, Contact link, Google Workspace Login */}
        <div className="global-brand-right">
          {/* Pricing Navigation Link */}
          <button
            onClick={onNavigatePricing}
            type="button"
            className="header-nav-link"
            title="Zobacz cennik i pakiety subskrypcyjne"
          >
            <Tag size={14} color="#60a5fa" />
            <span>Cennik</span>
          </button>

          {/* Security & Least Privilege Link */}
          <button
            onClick={onOpenSecurityModal}
            type="button"
            className="header-nav-link"
            title="Polityka bezpieczeństwa: Gwarancja 100% prywatności poczty"
          >
            <ShieldCheck size={15} color="#34d399" />
            <span className="security-pill-text">Bezpieczeństwo</span>
          </button>

          {/* Contact Modal Trigger */}
          {onOpenContactModal && (
            <button
              onClick={onOpenContactModal}
              type="button"
              className="header-nav-link"
              title="Skontaktuj się z zespołem CerebroLabs"
            >
              <span>Kontakt</span>
            </button>
          )}

          {/* Domain badge (when authenticated) */}
          {user && (
            <div
              className="navbar-domain-badge"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                background: '#111d31',
                border: '1px solid #1e2e45',
                padding: '5px 10px',
                borderRadius: 6,
                fontSize: 12,
                color: '#94a3b8',
              }}
            >
              <Building2 size={13} color="#60a5fa" />
              <span className="navbar-domain-label" style={{ color: '#94a3b8' }}>Domena:</span>
              <strong style={{ color: '#f8fafc' }}>{domain}</strong>
            </div>
          )}

          {/* User state: Google Workspace Login button vs User Profile */}
          {!user ? (
            <button
              onClick={onLogin}
              type="button"
              className="google-auth-btn-header"
              title="Zaloguj się kontem Google Workspace"
            >
              {/* Google G SVG */}
              <svg width="16" height="16" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                />
              </svg>
              <span>Zaloguj z Google Workspace</span>
              <ArrowRight size={13} style={{ color: '#64748b' }} />
            </button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName || 'Admin'}
                    style={{ width: 30, height: 30, borderRadius: '50%', border: '1.5px solid #0066FF' }}
                  />
                ) : (
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      background: '#111d31',
                      border: '1.5px solid #1e2e45',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <UserIcon size={15} color="#60a5fa" />
                  </div>
                )}
                <div className="navbar-user-text" style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: '#f8fafc', lineHeight: 1.2 }}>
                    {user.displayName || 'Administrator'}
                  </span>
                  <span style={{ fontSize: 11, color: '#64748b' }}>
                    {user.email}
                  </span>
                </div>
              </div>

              <button
                onClick={onLogout}
                className="btn"
                style={{
                  padding: '6px 10px',
                  fontSize: 12,
                  borderRadius: 6,
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  color: '#f1f5f9',
                }}
                title="Wyloguj się z panelu"
              >
                <LogOut size={13} />
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
export default Navbar;
